import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import qualityPlanItemService, { QualityPlanItem } from '../services/qualityPlanItem.service';

// Query keys
export const qualityPlanItemKeys = {
  all: ['quality-plan-items'] as const,
  lists: () => [...qualityPlanItemKeys.all, 'list'] as const,
  list: (planId: string) => [...qualityPlanItemKeys.lists(), planId] as const,
  details: () => [...qualityPlanItemKeys.all, 'detail'] as const,
  detail: (itemId: string) => [...qualityPlanItemKeys.details(), itemId] as const,
};

// Hooks
export const useQualityPlanItems = (planId: string, options?: { enabled?: boolean }): UseQueryResult<QualityPlanItem[], Error> => {
  return useQuery({
    queryKey: qualityPlanItemKeys.list(planId),
    queryFn: () => qualityPlanItemService.getAll(planId),
    enabled: !!planId && (options?.enabled ?? true),
  });
};

export const useQualityPlanItem = (planId: string, itemId: string): UseQueryResult<QualityPlanItem, Error> => {
  return useQuery({
    queryKey: qualityPlanItemKeys.detail(itemId),
    queryFn: () => qualityPlanItemService.getById(planId, itemId),
    enabled: !!planId && !!itemId,
  });
};

export const useCreateQualityPlanItem = (): UseMutationResult<
  QualityPlanItem,
  Error,
  { planId: string; data: Partial<QualityPlanItem> }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ planId, data }) => qualityPlanItemService.create(planId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityPlanItemKeys.list(variables.planId) });
      queryClient.invalidateQueries({ queryKey: ['quality-plans', variables.planId] });
      message.success('Inspection item created successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create inspection item');
    },
  });
};

export const useUpdateQualityPlanItem = (): UseMutationResult<
  QualityPlanItem,
  Error,
  { planId: string; itemId: string; data: Partial<QualityPlanItem> }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ planId, itemId, data }) => qualityPlanItemService.update(planId, itemId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityPlanItemKeys.list(variables.planId) });
      queryClient.invalidateQueries({ queryKey: qualityPlanItemKeys.detail(variables.itemId) });
      queryClient.invalidateQueries({ queryKey: ['quality-plans', variables.planId] });
      message.success('Inspection item updated successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update inspection item');
    },
  });
};

export const useDeleteQualityPlanItem = (): UseMutationResult<
  void,
  Error,
  { planId: string; itemId: string }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ planId, itemId }) => qualityPlanItemService.delete(planId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityPlanItemKeys.list(variables.planId) });
      queryClient.invalidateQueries({ queryKey: ['quality-plans', variables.planId] });
      message.success('Inspection item deleted successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to delete inspection item');
    },
  });
};

export const useReorderQualityPlanItems = (): UseMutationResult<
  QualityPlanItem[],
  Error,
  { planId: string; itemIds: string[] }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ planId, itemIds }) => qualityPlanItemService.reorder(planId, itemIds),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityPlanItemKeys.list(variables.planId) });
      message.success('Inspection items reordered successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to reorder inspection items');
    },
  });
};

export const useBulkCreateQualityPlanItems = (): UseMutationResult<
  QualityPlanItem[],
  Error,
  { planId: string; items: Partial<QualityPlanItem>[] }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ planId, items }) => qualityPlanItemService.bulkCreate(planId, items),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityPlanItemKeys.list(variables.planId) });
      queryClient.invalidateQueries({ queryKey: ['quality-plans', variables.planId] });
      message.success(`${variables.items.length} inspection items created successfully`);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create inspection items');
    },
  });
};
